import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'

export interface LicenseStatus {
  has_license: boolean
  tier: string | null
  tier_display?: string
  license_id?: string
  practice_name?: string
  expiry_date?: string | null
  days_until_expiry?: number
  is_expired?: boolean
  is_demo?: boolean
  warning?: 'critical' | 'warning' | 'notice' | 'expired' | null
  modules?: string[]
  edition?: 'lite' | 'full' | string  // LITE-4: drives Lite-only UI gating (e.g. hide tenant mgmt)
}

export interface LicenseActivationResult {
  status: string
  tier: string
  license_id: string
  expiry_date: string
  days_until_expiry: number
  modules: string[]
}

const licenseKeys = {
  all: ['license'] as const,
  status: () => [...licenseKeys.all, 'status'] as const,
}

export function useLicenseStatus() {
  const { authFetch } = useAuth()
  return useQuery<LicenseStatus>({
    queryKey: licenseKeys.status(),
    queryFn: async () => {
      const res = await authFetch('/api/license/status/')
      if (!res.ok) throw new Error('Failed to fetch license status')
      return res.json()
    },
    staleTime: 60 * 60_000,
    refetchInterval: 60 * 60_000,
  })
}

/**
 * LITE-6: single source of truth for the Lite edition check. `edition` comes
 * from GET /api/license/status/; used to hide multi-tenant admin surfaces in the
 * single-clinic Lite edition. Returns false while the status is still loading.
 */
export function useIsLite(): boolean {
  const { data } = useLicenseStatus()
  return data?.edition === 'lite'
}

export function useActivateLicense() {
  const { authFetch } = useAuth()
  const qc = useQueryClient()
  return useMutation<LicenseActivationResult, Error, { license_key: string }>({
    mutationFn: async ({ license_key }) => {
      const csrf = document.cookie.match('(^|;)\\s*csrftoken\\s*=\\s*([^;]+)')?.pop()
      const res = await authFetch('/api/license/activate/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRFToken': csrf } : {}),
        },
        body: JSON.stringify({ license_key }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Activation failed' }))
        throw new Error(err.detail || 'Activation failed')
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: licenseKeys.all })
      qc.invalidateQueries({ queryKey: ['feature-flags'] })
    },
  })
}
